import React, { useState, useEffect } from 'react';
import {
  InventoryCluster,
  ProjectInventory
} from '../../../types/inventory';
import { mockInventoryService } from '../../../services/mockInventoryService';
import Modal from '../../common/Modal';
import { confirmDelete, swalError } from '../../common/confirm';

interface ClusterDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  cluster: InventoryCluster | null;
}

interface ClusterMember extends ProjectInventory {
  relationship_type?: string;
  importance_level?: string;
  role_in_cluster?: string;
}

const ClusterDetailsModal: React.FC<ClusterDetailsModalProps> = ({ isOpen, onClose, cluster }) => {
  const [clusterMembers, setClusterMembers] = useState<ClusterMember[]>([]);
  const [availableItems, setAvailableItems] = useState<ProjectInventory[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'members' | 'dependencies' | 'analytics'>('overview');
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [relationshipType, setRelationshipType] = useState<'MEMBER' | 'CORE' | 'SUPPORTING' | 'DEPENDENT' | 'OPTIONAL'>('MEMBER');
  const [importanceLevel, setImportanceLevel] = useState<'CRITICAL' | 'HIGH' | 'NORMAL' | 'LOW'>('NORMAL');

  useEffect(() => {
    if (isOpen && cluster) {
      loadClusterData();
    }
  }, [isOpen, cluster]);

  const loadClusterData = async () => {
    if (!cluster) return;

    try {
      setLoading(true);
      const [membersResponse, itemsResponse] = await Promise.all([
        mockInventoryService.getClusterMembers(cluster.id),
        mockInventoryService.getInventoryItems()
      ]);

      setClusterMembers(membersResponse.data);
      // Filter out items that are already members of this cluster
      const memberIds = membersResponse.data.map(m => m.id);
      setAvailableItems(itemsResponse.data.filter(item => !memberIds.includes(item.id)));
    } catch (error) {
      console.error('Error loading cluster data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowAddMember(false);
    setSelectedItems([]);
    onClose();
  };

  const handleAddMembers = async () => {
    if (!cluster || selectedItems.length === 0) return;

    try {
      // In a real implementation, this would add cluster relationships
      // For now, we'll simulate by moving items from available to members
      const itemsToAdd = availableItems.filter(item => selectedItems.includes(item.id));
      const newMembers = itemsToAdd.map(item => ({
        ...item,
        relationship_type: relationshipType,
        importance_level: importanceLevel,
        role_in_cluster: undefined
      }));

      setClusterMembers(prev => [...prev, ...newMembers]);
      setAvailableItems(prev => prev.filter(item => !selectedItems.includes(item.id)));
      setSelectedItems([]);
      setShowAddMember(false);
    } catch (error) {
      console.error('Error adding cluster members:', error);
      swalError('Failed to add members. Please try again.');
    }
  };

  const handleRemoveMember = async (itemId: number) => {
    const ok = await confirmDelete({ title: 'Remove item from cluster?' });
    if (!ok) return;
    try {
      // In a real implementation, this would remove the cluster relationship
      const memberToRemove = clusterMembers.find(m => m.id === itemId);
      if (memberToRemove) {
        setClusterMembers(prev => prev.filter(m => m.id !== itemId));
        setAvailableItems(prev => [...prev, memberToRemove]);
      }
    } catch (error) {
      console.error('Error removing cluster member:', error);
      swalError('Failed to remove member. Please try again.');
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'ACTIVE': return '#28a745';
      case 'IN_PROGRESS': return '#007bff';
      case 'PLANNED': return '#ffc107';
      case 'DEPRECATED': return '#dc3545';
      case 'ARCHIVED': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'CRITICAL': return '#dc3545';
      case 'HIGH': return '#fd7e14';
      case 'MEDIUM': return '#ffc107';
      case 'LOW': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getRelationshipColor = (type: string): string => {
    switch (type) {
      case 'CORE': return '#dc3545';
      case 'MEMBER': return '#007bff';
      case 'SUPPORTING': return '#28a745';
      case 'DEPENDENT': return '#ffc107';
      case 'OPTIONAL': return '#6c757d';
      default: return '#6c757d';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>{cluster ? `${cluster.cluster_name} - Details` : 'Cluster Details'}</Modal.Header>
      <Modal.Body className="!p-0 overflow-hidden">
        <div className="modal-content">
          {/* Tab Navigation */}
          <div style={{
            display: 'flex',
            borderBottom: '2px solid #f8f9fa',
            marginBottom: '20px',
            padding: '0 24px'
          }}>
            {[
              { key: 'overview', label: 'Overview', icon: '📊' },
              { key: 'members', label: `Members (${clusterMembers.length})`, icon: '👥' },
              { key: 'dependencies', label: 'Dependencies', icon: '🔗' },
              { key: 'analytics', label: 'Analytics', icon: '📈' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                data-testid={`TAB_cluster_details_${tab.key}`}
                style={{
                  padding: '12px 20px',
                  border: 'none',
                  background: 'none',
                  borderBottom: activeTab === tab.key ? '3px solid #007bff' : '3px solid transparent',
                  color: activeTab === tab.key ? '#007bff' : '#6c757d',
                  fontWeight: activeTab === tab.key ? '600' : '400',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px'
                }}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {cluster && (
            <div style={{ padding: '0 24px 24px' }}>
              {activeTab === 'overview' && (
                <div>
                  <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                    {/* Cluster Information */}
                    <div>
                      <h3 style={{ margin: '0 0 16px 0', color: '#2c3e50', fontSize: '18px' }}>
                        Cluster Information
                      </h3>
                      <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px' }}>
                        <div style={{ marginBottom: '12px' }}>
                          <strong style={{ fontSize: '14px', color: '#495057' }}>Code:</strong>
                          <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '2px' }}>
                            {cluster.cluster_code}
                          </div>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <strong style={{ fontSize: '14px', color: '#495057' }}>Type:</strong>
                          <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '2px' }}>
                            {cluster.cluster_type}
                          </div>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <strong style={{ fontSize: '14px', color: '#495057' }}>Status:</strong>
                          <div style={{ marginTop: '4px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              backgroundColor: getStatusColor(cluster.cluster_status) + '20',
                              color: getStatusColor(cluster.cluster_status),
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              {cluster.cluster_status}
                            </span>
                          </div>
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <strong style={{ fontSize: '14px', color: '#495057' }}>Priority:</strong>
                          <div style={{ marginTop: '4px' }}>
                            <span style={{
                              color: getPriorityColor(cluster.priority_level),
                              fontWeight: '600',
                              fontSize: '13px'
                            }}>
                              {cluster.priority_level}
                            </span>
                          </div>
                        </div>
                        {cluster.business_domain && (
                          <div style={{ marginBottom: '12px' }}>
                            <strong style={{ fontSize: '14px', color: '#495057' }}>Domain:</strong>
                            <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '2px' }}>
                              {cluster.business_domain}
                            </div>
                          </div>
                        )}
                        {cluster.responsible_team && (
                          <div style={{ marginBottom: '12px' }}>
                            <strong style={{ fontSize: '14px', color: '#495057' }}>Team:</strong>
                            <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '2px' }}>
                              {cluster.responsible_team}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Progress and Stats */}
                    <div>
                      <h3 style={{ margin: '0 0 16px 0', color: '#2c3e50', fontSize: '18px' }}>
                        Progress & Statistics
                      </h3>
                      <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px' }}>
                        <div style={{ marginBottom: '20px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#495057' }}>
                              Completion Progress
                            </span>
                            <span style={{ fontSize: '14px', fontWeight: '600', color: '#007bff' }}>
                              {cluster.completion_percentage}%
                            </span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '12px',
                            backgroundColor: '#e9ecef',
                            borderRadius: '6px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${cluster.completion_percentage}%`,
                              height: '100%',
                              backgroundColor: '#007bff',
                              transition: 'width 0.3s ease'
                            }}></div>
                          </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                          <div style={{ textAlign: 'center', padding: '12px', background: 'white', borderRadius: '6px' }}>
                            <div style={{ fontSize: '24px', fontWeight: '700', color: '#007bff', marginBottom: '4px' }}>
                              {clusterMembers.length}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Total Members
                            </div>
                          </div>
                          <div style={{ textAlign: 'center', padding: '12px', background: 'white', borderRadius: '6px' }}>
                            <div style={{ fontSize: '24px', fontWeight: '700', color: '#28a745', marginBottom: '4px' }}>
                              {clusterMembers.filter(m => m.relationship_type === 'CORE').length}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Core Items
                            </div>
                          </div>
                          <div style={{ textAlign: 'center', padding: '12px', background: 'white', borderRadius: '6px' }}>
                            <div style={{ fontSize: '24px', fontWeight: '700', color: '#ffc107', marginBottom: '4px' }}>
                              {clusterMembers.filter(m => m.is_critical).length}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Critical Items
                            </div>
                          </div>
                          <div style={{ textAlign: 'center', padding: '12px', background: 'white', borderRadius: '6px' }}>
                            <div style={{ fontSize: '24px', fontWeight: '700', color: '#17a2b8', marginBottom: '4px' }}>
                              {Math.round(clusterMembers.reduce((sum, m) => sum + (m.test_coverage_percentage || 0), 0) / Math.max(clusterMembers.length, 1))}%
                            </div>
                            <div style={{ fontSize: '12px', color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Avg Test Coverage
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  {cluster.cluster_description && (
                    <div>
                      <h3 style={{ margin: '0 0 12px 0', color: '#2c3e50', fontSize: '18px' }}>
                        Description
                      </h3>
                      <div style={{
                        background: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: '#495057'
                      }}>
                        {cluster.cluster_description}
                      </div>
                    </div>
                  )}

                  {/* Documentation Notes */}
                  {cluster.documentation_notes && (
                    <div style={{ marginTop: '20px' }}>
                      <h3 style={{ margin: '0 0 12px 0', color: '#2c3e50', fontSize: '18px' }}>
                        Documentation Notes
                      </h3>
                      <div style={{
                        background: '#f8f9fa',
                        padding: '16px',
                        borderRadius: '8px',
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: '#495057'
                      }}>
                        {cluster.documentation_notes}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'members' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <div>
                      <h3 style={{ margin: '0', color: '#2c3e50', fontSize: '18px' }}>
                        Cluster Members ({clusterMembers.length})
                      </h3>
                      <p style={{ margin: '4px 0 0 0', color: '#6c757d', fontSize: '14px' }}>
                        Items that belong to this cluster
                      </p>
                    </div>
                    <button
                      onClick={() => setShowAddMember(true)}
                      data-testid="BTN_cluster_details_add_members"
                      style={{
                        padding: '10px 16px',
                        background: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      + Add Members
                    </button>
                  </div>

                  {showAddMember && (
                    <div style={{
                      background: '#f8f9fa',
                      padding: '20px',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      border: '2px solid #007bff'
                    }}>
                      <h4 style={{ margin: '0 0 16px 0', color: '#2c3e50' }}>Add Items to Cluster</h4>

                      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '4px', color: '#495057' }}>
                            Relationship Type:
                          </label>
                          <select
                            value={relationshipType}
                            onChange={(e) => setRelationshipType(e.target.value as any)}
                            data-testid="SEL_cluster_details_relationship_type"
                            style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                          >
                            <option value="MEMBER">Member</option>
                            <option value="CORE">Core</option>
                            <option value="SUPPORTING">Supporting</option>
                            <option value="DEPENDENT">Dependent</option>
                            <option value="OPTIONAL">Optional</option>
                          </select>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', marginBottom: '4px', color: '#495057' }}>
                            Importance Level:
                          </label>
                          <select
                            value={importanceLevel}
                            onChange={(e) => setImportanceLevel(e.target.value as any)}
                            data-testid="SEL_cluster_details_importance_level"
                            style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                          >
                            <option value="LOW">Low</option>
                            <option value="NORMAL">Normal</option>
                            <option value="HIGH">High</option>
                            <option value="CRITICAL">Critical</option>
                          </select>
                        </div>
                      </div>

                      <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '16px' }}>
                        {availableItems.map((item) => (
                          <div key={item.id} style={{
                            padding: '8px',
                            background: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            margin: '4px 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                          }}>
                            <input
                              type="checkbox"
                              checked={selectedItems.includes(item.id)}
                              data-testid={`CHK_cluster_details_item_${item.id}`}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedItems(prev => [...prev, item.id]);
                                } else {
                                  setSelectedItems(prev => prev.filter(id => id !== item.id));
                                }
                              }}
                            />
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '14px' }}>{item.name}</div>
                              <div style={{ fontSize: '12px', color: '#6c757d' }}>
                                {item.inventory_code}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={handleAddMembers}
                          disabled={selectedItems.length === 0}
                          data-testid="BTN_cluster_details_add_selected"
                          style={{
                            padding: '8px 16px',
                            background: selectedItems.length > 0 ? '#28a745' : '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: selectedItems.length > 0 ? 'pointer' : 'not-allowed',
                            fontSize: '14px'
                          }}
                        >
                          Add Selected ({selectedItems.length})
                        </button>
                        <button
                          onClick={() => {
                            setShowAddMember(false);
                            setSelectedItems([]);
                          }}
                          data-testid="BTN_cluster_details_add_cancel"
                          style={{
                            padding: '8px 16px',
                            background: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {loading ? (
                    <div className="loading-state">
                      <div className="loading-spinner"></div>
                      <p>Loading cluster members...</p>
                    </div>
                  ) : clusterMembers.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-icon">👥</div>
                      <h3>No Members Found</h3>
                      <p>This cluster doesn't have any members yet. Add some items to get started.</p>
                      <button
                        className="create-button"
                        onClick={() => setShowAddMember(true)}
                        data-testid="BTN_cluster_details_add_first"
                      >
                        Add First Member
                      </button>
                    </div>
                  ) : (
                    <div className="items-grid">
                      {clusterMembers.map((member) => (
                        <div key={member.id} className="item-card">
                          {/* Header Row: Code + Name */}
                          <div className="data_card_header">
                            <div className="code-name-row">
                              <span className="item-code">{member.inventory_code || 'NO-CODE'}</span>
                              <span className="item-name">{member.name || 'NO-NAME'}</span>
                            </div>
                          </div>

                          {/* Description */}
                          <div className="data_default">
                            {member.description || 'No description provided'}
                          </div>

                          <div className="item-metadata">
                            <div className="metadata-item">
                              <span className="label">Path</span>
                              <span className="value" style={{ fontSize: '11px' }}>
                                {member.folder_path}
                              </span>
                            </div>
                            <div className="metadata-item">
                              <span className="label">Language</span>
                              <span className="value">{member.programming_language || 'N/A'}</span>
                            </div>
                            <div className="metadata-item">
                              <span className="label">Complexity</span>
                              <span className="value">{member.estimated_complexity}</span>
                            </div>
                            <div className="metadata-item">
                              <span className="label">Test Coverage</span>
                              <span className="value">{member.test_coverage_percentage || 0}%</span>
                            </div>
                          </div>

                          <div className="item-actions">
                            <button
                              className="delete-button"
                              onClick={() => handleRemoveMember(member.id)}
                              data-testid={`BTN_cluster_details_member_${member.id}_remove`}
                              style={{ width: '100%' }}
                            >
                              Remove from Cluster
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'dependencies' && (
                <div>
                  <h3 style={{ margin: '0 0 20px 0', color: '#2c3e50', fontSize: '18px' }}>
                    Cluster Dependencies
                  </h3>
                  <div className="empty-state">
                    <div className="empty-icon">🔗</div>
                    <h3>Dependencies Analysis</h3>
                    <p>Dependency analysis features are coming soon. This will show relationships between cluster members and external components.</p>
                  </div>
                </div>
              )}

              {activeTab === 'analytics' && (
                <div>
                  <h3 style={{ margin: '0 0 20px 0', color: '#2c3e50', fontSize: '18px' }}>
                    Cluster Analytics
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '24px' }}>
                    <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: '#007bff', marginBottom: '8px' }}>
                        {clusterMembers.length}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6c757d', fontWeight: '600' }}>
                        Total Items
                      </div>
                    </div>

                    <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: '#28a745', marginBottom: '8px' }}>
                        {clusterMembers.filter(m => m.has_unit_tests).length}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6c757d', fontWeight: '600' }}>
                        With Unit Tests
                      </div>
                    </div>

                    <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: '#ffc107', marginBottom: '8px' }}>
                        {clusterMembers.filter(m => m.is_documented).length}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6c757d', fontWeight: '600' }}>
                        Documented
                      </div>
                    </div>

                    <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '32px', fontWeight: '700', color: '#dc3545', marginBottom: '8px' }}>
                        {clusterMembers.filter(m => m.is_deprecated).length}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6c757d', fontWeight: '600' }}>
                        Deprecated
                      </div>
                    </div>
                  </div>

                  {/* Programming Languages Chart */}
                  <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#2c3e50' }}>Programming Languages</h4>
                    {(() => {
                      const languages = clusterMembers.reduce((acc, member) => {
                        const lang = member.programming_language || 'Unknown';
                        acc[lang] = (acc[lang] || 0) + 1;
                        return acc;
                      }, {} as Record<string, number>);

                      return Object.entries(languages).map(([lang, count]) => (
                        <div key={lang} style={{ marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>{lang}</span>
                            <span style={{ fontSize: '14px', color: '#6c757d' }}>{count} items</span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: '#e9ecef',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${(count / clusterMembers.length) * 100}%`,
                              height: '100%',
                              backgroundColor: '#007bff'
                            }}></div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>

                  {/* Complexity Distribution */}
                  <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 16px 0', color: '#2c3e50' }}>Complexity Distribution</h4>
                    {(() => {
                      const complexities = clusterMembers.reduce((acc, member) => {
                        const complexity = member.estimated_complexity || 'UNKNOWN';
                        acc[complexity] = (acc[complexity] || 0) + 1;
                        return acc;
                      }, {} as Record<string, number>);

                      const colors: Record<string, string> = {
                        'LOW': '#28a745',
                        'MEDIUM': '#ffc107',
                        'HIGH': '#fd7e14',
                        'CRITICAL': '#dc3545',
                        'UNKNOWN': '#6c757d'
                      };

                      return Object.entries(complexities).map(([complexity, count]) => (
                        <div key={complexity} style={{ marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px', fontWeight: '500' }}>{complexity}</span>
                            <span style={{ fontSize: '14px', color: '#6c757d' }}>{count} items</span>
                          </div>
                          <div style={{
                            width: '100%',
                            height: '8px',
                            backgroundColor: '#e9ecef',
                            borderRadius: '4px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${(count / clusterMembers.length) * 100}%`,
                              height: '100%',
                              backgroundColor: colors[complexity] || '#6c757d'
                            }}></div>
                          </div>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ClusterDetailsModal;
